<template>
  <nuxt-link
    :to="link"
    :aria-label="game.name"
    :data-testid="testId([game.name, 'wrapper'])"
    class="group relative block aspect-[275/308] w-full overflow-hidden rounded-xl bg-depth-1 ring-2 ring-transparent transition-all duration-300 sm:hover:scale-[1.03] sm:hover:ring-brand"
  >
    <!-- Cover image -->
    <strapi-image-overlay
      v-if="game.coverImage"
      :data-testid="testId([game.name, 'cover-img'])"
      :image="game.coverImage"
      :alt-fallback="game.name"
      :ignore-overlay="isXbox"
      as-background
      class="absolute inset-0 h-full w-full"
    />

    <!-- Platform badge -->
    <div
      class="absolute left-0 top-0 m-2 flex size-7 items-center justify-center rounded-md bg-onyx/80 text-mouse backdrop-blur-xs sm:m-3 sm:size-8"
    >
      <common-platform-icon :platform="game.platform" class="size-4" />
    </div>

    <!-- Discount badge -->
    <span
      v-if="isDiscounted"
      class="absolute right-0 top-[45%] rounded-s bg-danger px-2 py-1 font-bold leading-relaxed text-mouse"
    >
      -{{ discountPercentage }}%
    </span>

    <!-- Bottom info overlay -->
    <div
      class="absolute inset-x-0 bottom-0 flex h-17.5 flex-col items-center justify-end overflow-hidden rounded-b-xl bg-onyx/80 p-2 backdrop-blur-xs transition-all duration-300 sm:group-hover:h-full"
    >
      <span
        :data-testid="testId([game.name, 'name'])"
        class="block max-w-full truncate font-bold text-mouse sm:group-hover:whitespace-normal"
      >
        {{ game.name }}
      </span>

      <!-- Price -->
      <span
        v-if="hasPricing"
        class="block max-w-full truncate text-mouse sm:group-hover:whitespace-normal"
        data-testid="game-price"
      >
        <i18n-t v-if="isDiscounted" keypath="landingpage/price_starting">
          <template #days>{{ game.startConfiguration!.duration }}</template>
          <template #price>
            <s class="text-rhino">{{ startingPrice }}</s>
            <strong class="ml-1 text-mouse">{{ discountedPrice }}</strong>
          </template>
        </i18n-t>
        <template v-else>
          {{
            t('landingpage/price_starting', {
              days: game.startConfiguration!.duration,
              price: startingPrice,
            })
          }}
        </template>
      </span>

      <!-- Order button (hover) -->
      <span
        v-if="hasPricing"
        data-testid="order-btn"
        class="mt-1 hidden w-full rounded-md bg-brand px-3 py-1.5 text-center font-bold uppercase tracking-wider text-onyx sm:group-hover:block"
      >
        {{ isPreOrder ? t('frontend/preorder-now') : t('frontend/order-now') }}
      </span>
    </div>
  </nuxt-link>
</template>

<script setup lang="ts">
import type { StrapiProduct } from '~/apollo/types/types';
import testId from '~/helpers/testid';

const { t } = useI18n();
const { formatStrapiPrice } = useNumberMoneyFormatting();

const props = defineProps<{ game: StrapiProduct }>();

const link = computed(() => `/${props.game.locale}/${props.game.slug}`);
const isXbox = computed(() => props.game.slug.includes('xbox'));
const isPreOrder = computed(() => timeIsAfterNow(props.game.releaseAt));

const hasPricing = computed(
  () => (props.game.startConfiguration?.prices?.length ?? 0) > 0,
);

const startingPrice = computed(() =>
  hasPricing.value
    ? formatStrapiPrice(props.game.startConfiguration!.prices[0])
    : '',
);

const isDiscounted = computed(() => props.game.isDiscountedGame);

const discountPercentage = computed(() =>
  Number(props.game.discountedPercentage ?? 0),
);

const discountedPrice = computed(() =>
  hasPricing.value
    ? formatStrapiPrice(
        props.game.startConfiguration!.prices[0],
        (props.game.discountedPercentage ?? 0) / 100,
      )
    : '',
);
</script>
